import { getDatapointTime } from './timeseries.js';
import {
  TimeseriesDatapoint,
  TimeseriesUnit,
} from '../../core/types/timeseries.js';
import { getRelativeValue } from '../../core/utils/get-relative-value.js';
import { getUnitAsString } from '../../core/utils/get-unit-as-string.js';
import { safeGet } from '../../core/utils/safe-get.js';
import { TimeseriesBandDataPoint } from '../types/timeseries-band.js';
import { ChartVariantInternal } from '../types/timeseries.js';

export const getDatapointKey = (timestamp: number, unit: string) =>
  `${timestamp}_${unit}`;

export function getDatapointValue(
  datapoint: TimeseriesDatapoint | TimeseriesBandDataPoint,
  variant: ChartVariantInternal,
  unit?: TimeseriesUnit,
  relativeGroupedValues?: {
    groupedValues: Map<string, number>;
    maxValuesByUnit: Map<string, number>;
    uniqueUnits: Map<string, boolean>;
  },
) {
  if (variant === 'band') {
    const { y0, y1 } = datapoint as TimeseriesBandDataPoint;
    if (relativeGroupedValues) {
      const seriesUnit = getUnitAsString(unit);
      const { maxValuesByUnit } = relativeGroupedValues;
      const total = safeGet(maxValuesByUnit, seriesUnit);

      const relativeY0Value = getRelativeValue(y0, total);
      const relativeY1Value = getRelativeValue(y1, total);

      return {
        y0: relativeY0Value,
        absoluteY0: y0,
        y1: relativeY1Value,
        absoluteY1: y1,
      };
    }

    return { y0, y1 };
  }

  const { value, start, end, center } = datapoint as TimeseriesDatapoint;

  if (relativeGroupedValues) {
    const { groupedValues, maxValuesByUnit } = relativeGroupedValues;

    const seriesUnit = getUnitAsString(unit);

    const datapointKey = getDatapointKey(
      getDatapointTime(start, end, center),
      seriesUnit,
    );

    const sumOfSeriesValues = safeGet(groupedValues, datapointKey) ?? 0;
    const isTotalValuePerUnit =
      relativeGroupedValues.uniqueUnits.get(seriesUnit) || variant === 'line';

    const total = isTotalValuePerUnit
      ? maxValuesByUnit.get(seriesUnit)
      : sumOfSeriesValues;

    // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
    const relativeValue = getRelativeValue(value, total!);

    return { absolute: value, value: relativeValue };
  }

  return { value };
}
